import React, {
  useMemo,
  ElementType,
} from 'react';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import {
  FileRejection,
} from 'react-dropzone';
import Dialog from 'pages/Dashboard/components/Dialog';
import Dropzone from 'pages/Dashboard/components/Dropzone';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Button,
} from '@populateui/ui';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  formatFileSize,
} from 'utils/string';
import {
  buildGeneratedPdf,
  formatElapsedSeconds,
  getEmptySelection,
  isImageFile,
  mergeImageSelections,
  ParseEOBSource,
  PendingEOBSelection,
  resolveEOBExtractionFile,
  shouldPreserveImageSelection,
  validateUploadedEOBFiles,
} from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/utils';
import {
  isEmptyString,
} from '@populateui/utils';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import usePreviewCarousel from 'pages/Dashboard/hooks/usePreviewCarousel';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import CameraIcon from 'core/icons/camera';
import ImageIcon from 'core/icons/image';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import IconButton from 'core/components/IconButton';
import {
  toLocalUrls,
} from 'utils/files';
import Spinner from 'pages/Dashboard/components/Spinner';
import ScanOverlay from 'core/components/ScanOverlay';
import PulsatingDots from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/PulsatingDots';
import {
  datadogLogs,
} from '@datadog/browser-logs';
import Divider from '@mui/material/Divider';
import EOBScanEstimation from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/EOBScanEstimation';

type Props = {
  source: ParseEOBSource | null;
  setSource: (source: ParseEOBSource | null) => void;
  pendingSelection: PendingEOBSelection;
  setPendingSelection: (selection: PendingEOBSelection) => void;
  isNormalizing: boolean;
  setIsNormalizing: (isNormalizing: boolean) => void;
  setFinalPayload: (file: File | null) => void;
  generatedPdfCache: FileLocalUrl | null;
  setGeneratedPdfCache: (cache: FileLocalUrl | null) => void;
  isExtracting: boolean;
  scanElapsedSeconds: number;
  startEOBExtraction: (file: File, source: ParseEOBSource) => Promise<void>;
  onCancel: () => void;
  onMinimize: () => void;
};

const SOURCE_OPTIONS: Array<{
  value: ParseEOBSource;
  label: string;
  helperText: string;
  icon: ElementType;
}> = [{
  value: 'pdf',
  label: 'Upload PDF',
  helperText: 'Upload a single PDF file',
  icon: PictureAsPdfOutlinedIcon,
}, {
  value: 'images',
  label: 'Upload Images',
  helperText: 'Upload one or more images',
  icon: ImageIcon,
}, {
  value: 'capture',
  label: 'Capture Images',
  helperText: 'Scan pages with your camera',
  icon: CameraIcon,
}];

function toPreviewMedia(file: FileLocalUrl): MultiMediaResponse | null {
  const fileName = file.file?.name ?? file.name ?? null;
  const url = file.dataUrl ?? (file.file ? URL.createObjectURL(file.file) : null);

  if (isNil(fileName) || isNil(url)) {
    return null;
  }

  return {
    fileName,
    url: { value: url },
  } as MultiMediaResponse;
}

export default function ParseEOBDialog({
  source,
  setSource,
  pendingSelection,
  setPendingSelection,
  isNormalizing,
  setIsNormalizing,
  setFinalPayload,
  generatedPdfCache,
  setGeneratedPdfCache,
  isExtracting,
  scanElapsedSeconds,
  startEOBExtraction,
  onCancel,
  onMinimize,
}: Props) {
  const onPreviewClick = usePreviewMediaClick();
  const onPreviewCarousel = usePreviewCarousel();

  function resetSelection(nextSource: ParseEOBSource | null) {
    setGeneratedPdfCache(null);
    setPendingSelection(getEmptySelection(nextSource));
  }

  function applyPendingSelection(result: PendingEOBSelection) {
    setGeneratedPdfCache(null);
    setPendingSelection(result);
  }

  async function getGeneratedPdf(): Promise<FileLocalUrl | null> {
    if (generatedPdfCache?.file) {
      return generatedPdfCache;
    }

    const generatedPdf = await buildGeneratedPdf(pendingSelection);
    setGeneratedPdfCache(generatedPdf);
    return generatedPdf;
  }

  const handlePdfDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      applyPendingSelection({
        ...getEmptySelection('pdf'),
        error: 'Only one PDF file is allowed.',
      });
      return;
    }

    setIsNormalizing(true);
    try {
      const nextSelection = await validateUploadedEOBFiles(acceptedFiles, 'pdf');
      applyPendingSelection(nextSelection);
    } finally {
      setIsNormalizing(false);
    }
  };

  const handleImageDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      const hasPdfRejection = fileRejections.some((rejection) => rejection.file?.type.includes('pdf')
        || rejection.file?.name.toLowerCase().endsWith('.pdf'));
      applyPendingSelection({
        ...pendingSelection,
        source: 'images',
        error: hasPdfRejection
          ? 'Please upload image files only.'
          : 'Please upload supported image files only.',
      });
      return;
    }

    setIsNormalizing(true);
    try {
      const nextSelection = await validateUploadedEOBFiles(acceptedFiles, 'images');
      if (nextSelection.error || nextSelection.kind !== 'images') {
        applyPendingSelection(nextSelection);
        return;
      }

      const uploadedFiles = await toLocalUrls(
        acceptedFiles.filter((file) => Boolean(file) && isImageFile(file)),
      );
      const mergedSelection = mergeImageSelections(pendingSelection, uploadedFiles, 'images');
      applyPendingSelection(mergedSelection);
    } finally {
      setIsNormalizing(false);
    }
  };

  const handlePdfUpdate = async (files: FileLocalUrl[]) => {
    setIsNormalizing(true);
    try {
      const nextSelection = await validateUploadedEOBFiles(
        files
          .map((item) => item.file)
          .filter((file): file is File => Boolean(file)),
        'pdf',
      );
      applyPendingSelection(nextSelection);
    } finally {
      setIsNormalizing(false);
    }
  };

  const handleSourceChange = (nextSource: ParseEOBSource) => {
    const currentImageSelection = pendingSelection.kind === 'images'
      ? pendingSelection
      : getEmptySelection('images');

    setSource(nextSource);

    if (shouldPreserveImageSelection(source, nextSource)) {
      setPendingSelection(currentImageSelection);
    } else {
      resetSelection(nextSource);
    }

    if (nextSource === 'capture') {
      mediaEventChannel.emit('onStartWebcamScan', {
        captureType: 'eob-camera-capture',
        multiple: true,
        limit: 10,
        outputMode: 'images',
        onCapturedFilesConfirmed: async (files) => {
          setIsNormalizing(true);
          try {
            const mergedSelection = mergeImageSelections(currentImageSelection, files, 'capture');
            applyPendingSelection(mergedSelection);
          } finally {
            setIsNormalizing(false);
          }
        },
      });
    }
  };

  const handleExtract = async () => {
    setIsNormalizing(true);
    try {
      const generatedPdf = pendingSelection.kind === 'images' && pendingSelection.files.length > 1
        ? await getGeneratedPdf()
        : generatedPdfCache;
      const extractionFile = await resolveEOBExtractionFile(
        pendingSelection,
        generatedPdf,
      );
      if (!isNil(extractionFile)) {
        if (extractionFile.size === 0) {
          applyPendingSelection({
            ...pendingSelection,
            error: 'The selected file may be damaged, empty, or unavailable on your device. Please check & try again.',
          });
          datadogLogs.logger.warn('[EOBExtraction] EOB extraction file is empty', {
            extractionFile: extractionFile?.name ?? null,
          });
          return;
        }
        setFinalPayload(extractionFile);
        await startEOBExtraction(extractionFile, source ?? 'pdf');
      }
    } catch (error) {
      console.error('Error extracting EOB document', error);
    } finally {
      setIsNormalizing(false);
    }
  };

  let imageDropError = 'Please upload supported image files only.';
  if (pendingSelection.error && source === 'images') {
    imageDropError = pendingSelection.error;
  }

  const previewMediaList = useMemo(
    () => pendingSelection.files
      .map((file) => toPreviewMedia(file))
      .filter((media): media is MultiMediaResponse => !isNil(media)),
    [pendingSelection.files],
  );

  const singlePreviewMedia = previewMediaList.length === 1 ? previewMediaList[0] : null;
  const hasValidSelection = pendingSelection.files.length > 0
    && isEmptyString(pendingSelection.error);
  const showPdfPreview = hasValidSelection
    && pendingSelection.kind === 'pdf'
    && !isNil(singlePreviewMedia);

  const isProcessing = isExtracting || isNormalizing;

  let summaryTypeLabel = 'Image file';
  if (pendingSelection.kind === 'pdf') {
    summaryTypeLabel = 'PDF file';
  } else if (pendingSelection.files.length > 1) {
    summaryTypeLabel = 'Images will be merged into a single PDF on Scan';
  }

  let summarySizeLabel = '';
  if (pendingSelection.kind === 'pdf') {
    summarySizeLabel = formatFileSize(pendingSelection.files[0]?.file?.size ?? 0);
  } else if (pendingSelection.files.length === 1) {
    summarySizeLabel = formatFileSize(pendingSelection.files[0]?.file?.size ?? 0);
  }

  const handlePreview = () => {
    if (previewMediaList.length === 1 && singlePreviewMedia) {
      onPreviewClick(singlePreviewMedia);
      return;
    }

    if (previewMediaList.length > 1) {
      onPreviewCarousel(previewMediaList);
    }
  };

  const onAddMore = () => {
    if (source === 'capture') {
      mediaEventChannel.emit('onStartWebcamScan', {
        captureType: 'eob-camera-capture',
        multiple: true,
        limit: 10,
        outputMode: 'images',
        onCapturedFilesConfirmed: async (files) => {
          setIsNormalizing(true);
          try {
            const mergedSelection = mergeImageSelections(pendingSelection, files, 'capture');
            applyPendingSelection(mergedSelection);
          } finally {
            setIsNormalizing(false);
          }
        },
      });
    }
  };

  return (
    <Dialog
      open
      title={(
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Scan EOB</h3>
          <p className="text-sm font-medium text-gray-500">AI-powered scan to auto-fill EOBs</p>
        </div>
      )}
      onClose={onCancel}
      disableBackdropClick={isProcessing}
      disableEscapeKeyDown={isProcessing}
      className="!w-[44rem] !space-y-4"
    >
      <div className="flex w-full flex-col gap-form-row">
        <div className={clsx('flex flex-col gap-1')}>
          <div className="text-sm font-semibold text-gray-700">Select document source</div>
          <fieldset className="flex flex-col gap-form-row">
            <legend className="sr-only">Select document source</legend>
            {SOURCE_OPTIONS.map((option) => {
              const selected = source === option.value;
              const Icon = option.icon;
              return (
                <label
                  key={option.value}
                  htmlFor={`eob-source-${option.value}`}
                  className={clsx(
                    'flex cursor-pointer items-center gap-2 rounded-lg d border p-2 transition-colors',
                    selected
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-400',
                    isProcessing && 'cursor-not-allowed opacity-70',
                  )}
                >
                  <input
                    id={`eob-source-${option.value}`}
                    type="radio"
                    name="eob-source"
                    value={option.value}
                    checked={selected}
                    onChange={() => { handleSourceChange(option.value); }}
                    onClick={() => {
                      if (option.value === 'capture' && selected && !isProcessing && !isNormalizing) {
                        handleSourceChange(option.value);
                      }
                    }}
                    className="sr-only"
                    disabled={isProcessing}
                  />
                  <span
                    className={clsx(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border',
                      selected
                        ? 'border-blue-200 bg-blue-500 text-white'
                        : 'border-gray-200 bg-gray-100 text-gray-600',
                    )}
                  >
                    <Icon className="!h-4 !w-4" />
                  </span>
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-0">
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className={clsx('text-sm font-semibold', {
                        'text-blue-600': selected,
                        'text-gray-800': !selected,
                      })}
                      >
                        {option.label}
                      </span>
                      <span className={clsx('text-xs', {
                        'text-blue-500': selected,
                        'text-gray-500': !selected,
                      })}
                      >
                        {option.helperText}
                      </span>
                    </span>
                    <span
                      className={clsx('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2', {
                        'border-blue-500': selected,
                        'border-gray-300': !selected,
                      })}
                    >
                      <span
                        className={clsx('h-2 w-2 rounded-full', {
                          'bg-blue-500': selected,
                          'bg-transparent': !selected,
                        })}
                      />
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>
        </div>
        {source === 'pdf' && !showPdfPreview && (
          <Dropzone
            onDrop={handlePdfDrop}
            accept={{ 'application/pdf': ['.pdf'] }}
            multiple={false}
            classes={{
              root: clsx(
                'flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-blue-300 bg-blue-50 px-4 py-4',
                'hover:bg-blue-100 transition-colors',
              ),
              text: 'text-xs font-medium text-blue-700',
            }}
            placeholder="Drop your PDF here or click to browse"
            rejectPlaceholder="Only PDF files are supported"
            rejectMultiplePlaceholder="Only one PDF file is allowed."
            disabled={isProcessing}
          />
        )}

        {source === 'images' && (
          <Dropzone
            onDrop={handleImageDrop}
            accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.tiff'] }}
            multiple
            classes={{
              root: clsx(
                'flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-blue-300 bg-blue-50 px-4 py-4',
                'hover:bg-blue-100 transition-colors',
              ),
              text: 'text-xs font-medium text-blue-700',
            }}
            placeholder="Drop one or more images here or click to browse"
            rejectPlaceholder={imageDropError}
            disabled={isProcessing}
          />
        )}

        {pendingSelection.error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {pendingSelection.error}
          </div>
        )}
        {showPdfPreview && (
          <ScanOverlay
            active={isProcessing}
            classes={{
              root: 'w-full max-h-[120px] rounded-md',
              content: 'overflow-y-hidden rounded-md',
            }}
          >
            <MediaPreview
              media={singlePreviewMedia}
              onDelete={() => { resetSelection(source); }}
              onUpdate={isProcessing ? undefined : handlePdfUpdate}
              mediaType={MultimediaType.EOB}
              className="w-full"
              hasDelete={!isProcessing}
              thumbnail
            />
          </ScanOverlay>
        )}
        {hasValidSelection && !showPdfPreview && (
          <ScanOverlay
            active={isProcessing}
            classes={{
              root: 'rounded-md',
            }}
          >
            <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <ArticleRoundedIcon className="!mt-0.5 !h-4 !w-4 !text-blue-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-gray-800">
                  {pendingSelection.summaryLabel}
                </p>
                <p className="text-[11px] leading-4 text-gray-500">
                  {summaryTypeLabel}
                  {!isEmptyString(summarySizeLabel) ? ` · ${summarySizeLabel}` : ''}
                </p>
              </div>
              {!isProcessing && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="text"
                    size="small"
                    disabled={previewMediaList.length === 0 || isProcessing}
                    onClick={handlePreview}
                  >
                    Preview
                  </Button>
                  <Button
                    disabled={isProcessing}
                    variant="text"
                    size="small"
                    onClick={() => { resetSelection(source); }}
                  >
                    Remove
                  </Button>
                  {source === 'capture' && (
                    <Button
                      variant="text"
                      size="small"
                      disabled={isProcessing}
                      onClick={onAddMore}
                    >
                      Add more
                    </Button>
                  )}
                </div>
              )}
            </div>
          </ScanOverlay>
        )}
        {hasValidSelection && (
          <div className="flex flex-col gap-2 text-xs font-semibold text-gray-700 -mt-1.5 py-2 px-2 bg-blue-100 rounded-md">
            {isProcessing && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <PulsatingDots className="text-inherit" />
                  <span className="text-inherit">Processing claims...</span>
                  <Divider className="!h-4 !bg-gray-600" orientation="vertical" flexItem />
                  <span className="text-xs text-gray-700 font-medium">
                    Elapsed:
                    {' '}
                    {formatElapsedSeconds(scanElapsedSeconds)}
                  </span>
                </div>
                <IconButton
                  className="!w-8 !h-8"
                  tooltip="Minimize — scanning continues in the background"
                  onClick={onMinimize}
                >
                  <CloseFullscreenRoundedIcon className="!w-4 !h-4" />
                </IconButton>
              </div>
            )}
            <EOBScanEstimation />
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!hasValidSelection || isProcessing}
            onClick={handleExtract}
          >
            {isProcessing ? (
              <div className="flex items-center gap-1">
                <Spinner className="!text-inherit" />
                Scanning...
              </div>
            ) : 'Scan'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
